import React, { Component } from "react";
import {
  Table,
  Row,
  Col,
  Button,
  Spinner,
  Modal,
  Form,
  FormControl,
} from "react-bootstrap";
import CartContext from "../StoreManager/CartContext";
import { fetchCrmList, saveCrmItem, deleteCrmItem } from "./CrmApi";

const emptyPlan = {
  servicePlanId: "",
  storeId: "",
  contactId: "",
  assignedUserId: "",
  planName: "",
  description: "",
  startDate: "",
  endDate: "",
  status: "",
  totalCost: "",
};

export default class CrmServicePlans extends Component {
  static contextType = CartContext;

  constructor(props) {
    super(props);
    this.state = {
      plans: [],
      filteredPlans: [],
      loading: true,
      search: "",
      error: null,
      showModal: false,
      isEdit: false,
      currentPlan: { ...emptyPlan },
    };
  }

  componentDidMount() {
    this.loadPlans();
  }

  loadPlans = async () => {
    const storeId = this.context.storeId;
    if (!storeId) {
      this.setState({ loading: false, error: "StoreId is required." });
      return;
    }

    this.setState({ loading: true, error: null });
    try {
      const plans = await fetchCrmList(
        "ServicePlans",
        "GetServicePlans",
        storeId,
      );
      const normalized = Array.isArray(plans) ? plans : [];
      this.setState({
        plans: normalized,
        filteredPlans: normalized,
        loading: false,
      });
    } catch (error) {
      this.setState({
        loading: false,
        error: error.message || "Unable to load service plans.",
      });
    }
  };

  onSearchChange = (event) => {
    const search = event.target.value || "";
    const normalizedSearch = search.trim().toLowerCase();
    const filteredPlans = this.state.plans.filter((plan) => {
      const name = (plan.planName || "").toLowerCase();
      const status = (plan.status || "").toLowerCase();
      const description = (plan.description || "").toLowerCase();
      return (
        name.includes(normalizedSearch) ||
        status.includes(normalizedSearch) ||
        description.includes(normalizedSearch)
      );
    });
    this.setState({ search, filteredPlans });
  };

  handleShowModal = (plan = null) => {
    this.setState({
      currentPlan: plan ? { ...plan } : { ...emptyPlan },
      isEdit: Boolean(plan),
      showModal: true,
      error: null,
    });
  };

  handleCloseModal = () => {
    this.setState({ showModal: false, error: null });
  };

  handleFieldChange = (field, value) => {
    this.setState((prevState) => ({
      currentPlan: {
        ...prevState.currentPlan,
        [field]: value,
      },
    }));
  };

  savePlan = async () => {
    const storeId = this.context.storeId;
    if (!storeId) {
      this.setState({ error: "StoreId is required." });
      return;
    }

    const plan = {
      ...this.state.currentPlan,
      storeId,
      servicePlanId:
        this.state.currentPlan.servicePlanId ||
        "00000000-0000-0000-0000-000000000000",
      totalCost: this.state.currentPlan.totalCost
        ? Number(this.state.currentPlan.totalCost)
        : null,
    };

    if (!plan.planName) {
      this.setState({ error: "Plan name is required." });
      return;
    }

    try {
      await saveCrmItem("ServicePlans", "AddOrUpdateServicePlan", plan);
      await this.loadPlans();
      this.handleCloseModal();
    } catch (error) {
      this.setState({ error: error.message || "Unable to save service plan." });
    }
  };

  deletePlan = async (planId) => {
    const storeId = this.context.storeId;
    if (!storeId) return;
    if (!window.confirm("Delete this service plan?")) return;

    try {
      await deleteCrmItem("ServicePlans", "DeleteServicePlan", storeId, planId);
      await this.loadPlans();
    } catch (error) {
      this.setState({
        error: error.message || "Unable to delete service plan.",
      });
    }
  };

  formatDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString();
  };

  render() {
    const {
      filteredPlans,
      loading,
      search,
      error,
      showModal,
      isEdit,
      currentPlan,
    } = this.state;
    const storeId = this.context.storeId;

    return (
      <div className="m-3">
        <Row className="align-items-center mb-3">
          <Col>
            <h2>CRM Service Plans</h2>
            <p className="text-muted">
              Manage service plans for the selected store.
            </p>
          </Col>
          <Col xs="auto" className="d-flex gap-2">
            <Button variant="primary" onClick={() => this.handleShowModal()}>
              Add Service Plan
            </Button>
            <Button
              variant="secondary"
              onClick={this.loadPlans}
              disabled={loading}
            >
              {loading ? <Spinner animation="border" size="sm" /> : "Refresh"}
            </Button>
          </Col>
        </Row>

        {!storeId && (
          <div className="alert alert-warning">
            StoreId is required to load CRM service plans.
          </div>
        )}
        {error && <div className="alert alert-danger">{error}</div>}

        <Row className="mb-3">
          <Col md={6} lg={4}>
            <FormControl
              placeholder="Search by plan name, status, or description"
              value={search}
              onChange={this.onSearchChange}
            />
          </Col>
        </Row>

        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Name</th>
              <th>Status</th>
              <th>ContactId</th>
              <th>AssignedUserId</th>
              <th>Start</th>
              <th>End</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" className="text-center py-5">
                  <Spinner animation="border" /> Loading...
                </td>
              </tr>
            ) : filteredPlans.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center py-5">
                  No service plans found.
                </td>
              </tr>
            ) : (
              filteredPlans.map((plan) => (
                <tr key={plan.servicePlanId || Math.random()}>
                  <td>{plan.planName || "-"}</td>
                  <td>{plan.status || "-"}</td>
                  <td>{plan.contactId || "-"}</td>
                  <td>{plan.assignedUserId || "-"}</td>
                  <td>{this.formatDate(plan.startDate)}</td>
                  <td>{this.formatDate(plan.endDate)}</td>
                  <td>
                    <Button
                      size="sm"
                      variant="outline-primary"
                      className="me-2"
                      onClick={() => this.handleShowModal(plan)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => this.deletePlan(plan.servicePlanId)}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>

        <Modal show={showModal} onHide={this.handleCloseModal}>
          <Modal.Header closeButton>
            <Modal.Title>
              {isEdit ? "Edit Service Plan" : "Add Service Plan"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3" controlId="planName">
                <Form.Label>Plan Name</Form.Label>
                <Form.Control
                  type="text"
                  value={currentPlan.planName}
                  onChange={(e) =>
                    this.handleFieldChange("planName", e.target.value)
                  }
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="description">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={currentPlan.description}
                  onChange={(e) =>
                    this.handleFieldChange("description", e.target.value)
                  }
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="contactId">
                <Form.Label>ContactId</Form.Label>
                <Form.Control
                  type="text"
                  value={currentPlan.contactId}
                  onChange={(e) =>
                    this.handleFieldChange("contactId", e.target.value)
                  }
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="assignedUserId">
                <Form.Label>Assigned UserId</Form.Label>
                <Form.Control
                  type="text"
                  value={currentPlan.assignedUserId}
                  onChange={(e) =>
                    this.handleFieldChange("assignedUserId", e.target.value)
                  }
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="status">
                <Form.Label>Status</Form.Label>
                <Form.Control
                  type="text"
                  value={currentPlan.status}
                  onChange={(e) =>
                    this.handleFieldChange("status", e.target.value)
                  }
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="startDate">
                <Form.Label>Start Date</Form.Label>
                <Form.Control
                  type="date"
                  value={
                    currentPlan.startDate
                      ? currentPlan.startDate.substring(0, 10)
                      : ""
                  }
                  onChange={(e) =>
                    this.handleFieldChange("startDate", e.target.value)
                  }
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="endDate">
                <Form.Label>End Date</Form.Label>
                <Form.Control
                  type="date"
                  value={
                    currentPlan.endDate
                      ? currentPlan.endDate.substring(0, 10)
                      : ""
                  }
                  onChange={(e) =>
                    this.handleFieldChange("endDate", e.target.value)
                  }
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="totalCost">
                <Form.Label>Total Cost</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  value={currentPlan.totalCost}
                  onChange={(e) =>
                    this.handleFieldChange("totalCost", e.target.value)
                  }
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={this.handleCloseModal}>
              Cancel
            </Button>
            <Button variant="primary" onClick={this.savePlan}>
              Save
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }
}
